import React, { useCallback, useEffect, useState } from 'react';
import { Laptop2, LogOut, MapPin, ShieldCheck } from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { errorMessage } from './ui';
import { tx } from './i18n';

// Where this account is signed in, and the button that ends one of them.
// Extracted from the shell so Settings can own it: it is a thing you manage,
// not part of the frame around the page.

export function SessionList() {
  const { loadSessions, revokeSession } = useXomicsAuth();
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await loadSessions();
      setSessions(result.sessions || []);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [loadSessions]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function revoke(sessionUuid) {
    try {
      await revokeSession(sessionUuid);
      await refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  return (
    <section className="xo-account-settings-panel xo-session-settings">
      <header>
        <span><ShieldCheck aria-hidden="true" /></span>
        <div>
          <p className="xo-eyebrow">{tx("ui.sessions.security_f25ce1b8")}</p>
          <h2>{tx("ui.sessions.active_sessions_e58e7782")}</h2>
          <p>{sessions.filter((session) => session.active).length.toLocaleString()} {tx("ui.sessions.active_a733b809").toLowerCase()}</p>
        </div>
      </header>
      {loading && <p className="xo-muted">{tx("ui.sessions.loading_sessions_31640fe9")}</p>}
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {!loading && sessions.length === 0 && (
        <p className="xo-muted">{tx("ui.sessions.no_sessions_found_328c1aa9")}</p>
      )}
      <div className="xo-session-list">
        {sessions.map((session) => (
          <article key={session.sessionUuid}>
            <span className="xo-session-device"><Laptop2 aria-hidden="true" /></span>
            <div className="xo-session-copy">
              <strong>{session.current ? tx("ui.sessions.this_device_fa5a6dd9") : tx("ui.sessions.signed_in_device_05c8ddec")}</strong>
              <span>{session.userAgent || tx("ui.sessions.unknown_browser_58c51e22")}</span>
              <small>
                <MapPin aria-hidden="true" />
                {[session.countryCode, session.ipAddress].filter(Boolean).join(' · ')}
              </small>
            </div>
            <div className="xo-session-actions">
              <span className={`xo-settings-state${session.active ? ' is-active' : ''}`}>
                <i aria-hidden="true" />
                {session.active ? tx("ui.sessions.active_a733b809") : tx("ui.sessions.ended_90303d8d")}
              </span>
              {session.active && (
                <button
                  type="button"
                  className="xo-link"
                  onClick={() => revoke(session.sessionUuid)}
                >
                  <LogOut aria-hidden="true" />{tx("ui.sessions.sign_out_dc1649a1")}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
